import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { OrganizationInvitation } from '../data-models/organization-invitation.model';
import { InvitationRequest } from '../request-models/invitation-request.model';
import { InvitationListResponse } from '../response-models/invitation-list-response.model';
import { InvitationResendResult } from '../response-models/invitation-resend-result.model';
import { InvitationStats } from '../response-models/invitation-stats.model';
import { toInvitationRequest } from '../utils/invitation-request-utils';
import { RoleHelper } from '../utils/membership-role-utils';
import { isRecord } from '../utils/is-record';
import { countOrZero, nullableString } from '../utils/wire-value-utils';
import { AuthService } from '../services/auth.service';
import { OrganizationContextService } from '../services/organization-context.service';
import { orgPrefix } from '../utils/org-path-utils';

type InvitationStatusFilter = 'pending' | 'accepted' | 'cancelled' | 'expired' | 'all';

/**
 * Organization invitations: `/organizations/:id/invitations[/:invitationId[/resend]]` and
 * `/invitations/:invitationId/accept` (hub `user-organization-invitations-controller.ts`).
 * Memberships live in `TeamService`; public token lookups (accept-invitation page) in `AuthInvitationService`.
 *
 * Holds the invitations of the last loaded organization as signal state.
 * Failures reject with an `ApiError` (whose `message` is the hub's error).
 */
@Injectable({
  providedIn: 'root'
})
export class InvitationService extends BaseService<OrganizationInvitation> {
  private authService = inject(AuthService);
  private organizationContext = inject(OrganizationContextService);

  private invitationsSignal = signal<OrganizationInvitation[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  readonly invitations = computed(() => this.invitationsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly pendingInvitations = computed(() =>
    this.invitationsSignal().filter(i => !i.isAccepted && !i.isCancelled && !isExpired(i)).length
  );

  constructor() {
    super('invitations', OrganizationInvitation);
  }

  /** Load an organization's invitations into `invitations`. Failures set `error` instead of throwing. */
  async loadInvitations(organizationId: string, status: InvitationStatusFilter = 'pending'): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const list = await firstValueFrom(
        this.request('GET', { prefix: orgPrefix(organizationId), query: { status } }, data => this.toInvitationList(data))
      );
      this.invitationsSignal.set(list.invitations);
    } catch (error: unknown) {
      this.errorSignal.set(ApiError.fromHttpError(error).errors[0] ?? 'Failed to load invitations');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** Invite someone to an organization (Owner / Chief Supervisor only). */
  async createInvitation(organizationId: string, dto: InvitationRequest): Promise<OrganizationInvitation | null> {
    const created = await this.track(
      firstValueFrom(this.create(toInvitationRequest(dto), { prefix: orgPrefix(organizationId) }))
    );
    if (created) {
      this.invitationsSignal.update(invitations => [...invitations, created]);
    }
    return created;
  }

  /** Resend an invitation email. The hub limits this to one resend per 24 hours and five in total. */
  async resendInvitation(organizationId: string, invitationId: string): Promise<void> {
    const opts = { prefix: orgPrefix(organizationId), id: invitationId, path: 'resend' };
    const result = await this.track(firstValueFrom(this.request('POST', opts, toResendResult, {})));
    const invitation = this.invitationsSignal().find(i => i.id === invitationId);
    if (invitation) {
      invitation.resendCount = result.resendCount;
      invitation.lastResentAt = result.lastResentAt;
      this.invitationsSignal.update(invitations => [...invitations]);
    }
  }

  /** Cancel a pending invitation. */
  async cancelInvitation(organizationId: string, invitationId: string): Promise<void> {
    await this.track(firstValueFrom(this.delete(invitationId, { prefix: orgPrefix(organizationId) })));
    this.invitationsSignal.update(invitations => invitations.filter(i => i.id !== invitationId));
  }

  /**
   * Accept an invitation as the signed-in user, then refresh the token (for the new membership claims)
   * and switch to the joined organization. Returns that organization's id when the hub sends it.
   */
  async acceptInvitation(invitationId: string): Promise<string | null> {
    const opts = { id: invitationId, path: 'accept' };
    const read = (data: unknown) => (isRecord(data) ? nullableString(data['organizationId']) ?? null : null);
    const organizationId = await this.track(firstValueFrom(this.request('POST', opts, read, {})));
    await this.authService.refreshToken();
    if (organizationId) {
      this.organizationContext.setCurrentOrganization(organizationId);
    }
    return organizationId;
  }

  clearError(): void {
    this.errorSignal.set(null);
  }

  /** Clear all cached state (e.g. when switching organizations). */
  reset(): void {
    this.invitationsSignal.set([]);
    this.errorSignal.set(null);
    this.loadingSignal.set(false);
  }

  /** Runs an action with the loading flag set, recording its error message before rethrowing. */
  private async track<R>(action: Promise<R>): Promise<R> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      return await action;
    } catch (error: unknown) {
      const apiError = ApiError.fromHttpError(error);
      this.errorSignal.set(apiError.message);
      throw apiError;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  private toInvitationList(data: unknown): InvitationListResponse {
    const source = isRecord(data) ? data : {};
    const items = source['invitations'];
    const invitations = Array.isArray(items) ? items.map(item => this.createType(item)) : [];
    return { invitations, stats: toInvitationStats(source['stats']) };
  }
}


function isExpired(invitation: OrganizationInvitation): boolean {
  return !!invitation.expiresAt && RoleHelper.isExpired(invitation.expiresAt);
}

function toResendResult(data: unknown): InvitationResendResult {
  const source = isRecord(data) ? data : {};
  return { resendCount: countOrZero(source['resendCount']), lastResentAt: nullableString(source['lastResentAt']) ?? null };
}

function toInvitationStats(data: unknown): InvitationStats {
  const source = isRecord(data) ? data : {};
  return {
    total: countOrZero(source['total']),
    pending: countOrZero(source['pending']),
    accepted: countOrZero(source['accepted']),
    cancelled: countOrZero(source['cancelled']),
    expired: countOrZero(source['expired']),
  };
}
